<template>
  <div class="content">
    <div class="page-inner">
      <h1 class="mb-4">
        Lyric Videos <span class="badge-pro badge-primary bg-gradient text-white fw-bold px-2 py-1">PRO</span>
      </h1>

      <div class="card" v-if="lyricVideo.status === 2">
        <div class="card-body">
          <p>
            {{ $t('lyric_video_now_available') }}
          </p>
          <a :href="lyricVideo.video_url" target="_blank" class="btn btn-default">
            {{ $t('download') }}
          </a>
        </div>
      </div>

      <div class="card" v-if="lyricVideo.status === 1">
        <div class="card-body">
          <i class="fa fa-spinner fa-spin mr-1"></i> {{ $t('generating_lyric_video') }}
        </div>
        <div class="card-footer text-right">
          <router-link to="/tools/lyric-videos" class="btn btn-light">
            {{ $t('back') }}
          </router-link>
        </div>
      </div>

      <div id="accordion" class="mb-4" v-if="lyricVideo.status < 1">
        <div class="card">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left">
              {{ $t('choose_song') }}
            </h4>
          </div>

          <div class="card-body">
            <div class="row">
              <div :class="{ 'col-md-8': getVideoPreview(), 'col-md-12': !getVideoPreview() }">
                <div class="form-group">
                  <i class="fa fa-spinner fa-spin" v-if="loading"></i>
                  <label for="lyricVideoingFile" v-if="distributions.length">{{ $t('release') }}</label>
                  <div class="select2-input" v-if="distributions.length">
                    <Select2 v-model="lyricVideo.distribution" :options="distributionOptions"></Select2>
                  </div>
                  <p v-if="!loading && !distributions.length">
                    {{ $t('zero_published_releases') }}
                  </p>
                </div>
                <div class="form-group" v-if="songs.length">
                  <label for="lyricVideoingFile">{{ $t('song') }}</label>
                  <div class="select2-input">
                    <Select2 v-model="currentSong" :options="songs"></Select2>
                  </div>
                </div>
              </div>
              <div class="col-md-4" v-if="getVideoPreview()">
                <div class="form-group">
                  <label for="">DEMO Preview</label>
                  <div>
                    <video width="100%" controls controlsList="nodownload" :src="getVideoPreview()"></video>
                  </div>
                  <div class="mt-2">
                    <i class="fa fa-info text-info mr-1"></i> {{ $t('watermarks_will_be_removed') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card" v-if="lyricVideo.song_index !== undefined">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left">
              Lyrics
            </h4>
          </div>
          <div class="card-body" v-if="transcriptionLoaded && !lyricTranscription.segments">
            <span v-if="!hasLyrics()">
              {{ $t('track_does_not_have_lyrics') }}
            </span>
            <button type="button" class="btn btn-primary" v-if="hasLyrics() && !lyricVideo.transcribing" @click="submit(0, 1)">
              {{ $t('generate_lyrics') }}
            </button>
            <span v-if="lyricVideo.transcribing">
              <i class="fa fa-spinner fa-spin mr-1"></i> {{ $t('we_will_transcribe') }}
            </span>
          </div>
          <div class="card-body" v-if="lyricTranscription.segments">
            <audio ref="audioPreview" :src="getAudioUrl()" @timeupdate="onTimeUpdate()" preload="true"></audio>
            <div class="form-group" v-for="(segment, index) in lyricTranscription.segments" v-bind:key="`segment_${index}`" :class="{ 'has-success': segmentIndex === index }">
              <label for="">{{ parseInt(segment.start) }}s -> {{ parseInt(segment.end) }}s</label>
              <div class="input-group-append">
                <input type="text" v-model="segment.text" class="form-control">
                <button type="button" :disabled="segmentIndex === index" class="btn btn-black btn-border" @click="previewSegment(segment.start, segment.end, index)">
                  <i class="fa fa-play"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="card-footer text-right" v-if="transcriptionLoaded && !lyricTranscription.segments">
            <router-link to="/tools/lyric-videos" class="btn btn-light">
              {{ $t('back') }}
            </router-link>
          </div>
        </div>

        <div class="card mb-4" v-if="lyricVideo.song_index !== undefined && lyricTranscription.segments">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left">
              {{ $t('video_style') }}
            </h4>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label for="">{{ $t('background_color') }}</label>
              <div class="mt-2">
                <button @click="setColor(1)" type="button" class="btn btn-light" :disabled="lyricVideo.color == 1">
                  <i class="fa fa-square fa-2x color-1"></i>
                </button>
                <button @click="setColor(2)" type="button" class="btn btn-light ml-1" :disabled="lyricVideo.color == 2">
                  <i class="fa fa-square fa-2x color-2"></i>
                </button>
                <button @click="setColor(3)" type="button" class="btn btn-light" :disabled="lyricVideo.color == 3">
                  <i class="fa fa-square fa-2x color-3"></i>
                </button>
                <button @click="setColor(4)" type="button" class="btn btn-light" :disabled="lyricVideo.color == 4">
                  <i class="fa fa-square fa-2x color-4"></i>
                </button>
                <button @click="setColor(5)" type="button" class="btn btn-light ml-1" :disabled="lyricVideo.color == 5">
                  <i class="fa fa-square fa-2x color-5"></i>
                </button>
                <button @click="setColor(6)" type="button" class="btn btn-light" :disabled="lyricVideo.color == 6">
                  <i class="fa fa-square fa-2x color-6"></i>
                </button>
              </div>
            </div>
            <div class="p-3 text-right mt-2">
              <button type="button" class="btn btn-lg btn-light btn-border mr-1 mb-1" @click="submit(0)">
                {{ $t('save_draft') }}
              </button>
              <button type="button" class="btn btn-lg btn-primary mb-1" @click="submit(1)">
                {{ $t('submit') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'underscore'
import LyricVideo from '../services/lyricvideo'
import Distributions from '../services/distributions'
import Select2 from './utils/Select2'

export default {
  name: 'LyricVideoProcess',
  data: function () {
    return {
      loading: true,
      transcriptionLoaded: false,
      distributions: [],
      distributionOptions: [],
      lyricVideo: {
        status: 0,
        color: 1
      },
      currentSong: null,
      previewStart: 0,
      previewEnd: 0,
      lyricTranscription: {},
      segmentIndex: null
    }
  },
  methods: {
    hasLyrics: function () {
      let distribution = _.find(this.distributions, item => item._id === this.lyricVideo.distribution)

      if (distribution && distribution.songs[this.currentSong] && distribution.songs[this.currentSong].performance_language !== 'ZXX') {
        return true
      }
    },
    setColor: function (color) {
      this.lyricVideo.color = color
      this.$forceUpdate()
    },
    submit: function (status, transcribing) {
      window.swal({
        title: this.$t('are_you_sure'),
        text: this.$t('lyrics_will_updated'),
        buttons: {
          cancel: {
            visible: true,
            text: this.$t('cancel'),
            className: 'btn btn-default btn-border no-border'
          },
          confirm: {
            text: this.$t('ok'),
            className: 'btn btn-primary'
          }
        }
      }).then(confirm => {
        if (confirm) {
          if (transcribing) {
            this.lyricVideo.transcribing = 1
          }

          this.lyricVideo.lyric_transcription = this.lyricTranscription._id
          this.lyricVideo.segments = this.lyricTranscription.segments
          this.lyricVideo.status = status

          _.each(this.lyricTranscription.segments, segment => {
            segment.text = segment.text.replace(/\n/g, '')
          })

          LyricVideo.save(this.lyricVideo).then(() => {
            this.$router.push('/tools/lyric-videos')
          })
        }
      })
    },
    getVideoPreview: function () {
      if (this.lyricVideo.distribution && this.currentSong !== null) {
        let distro = _.find(this.distributions, item => {
          return item._id === this.lyricVideo.distribution
        })

        let song = distro.songs[this.lyricVideo.song_index]

        if (song) {
          return song.lyricvideo_demo
        }
      }
    },
    onTimeUpdate: function () {
      if (this.$refs.audioPreview && this.$refs.audioPreview.currentTime >= this.previewEnd) {
        this.$refs.audioPreview.pause()
        this.segmentIndex = null
      }
    },
    previewSegment: function (start, end, index) {
      this.$refs.audioPreview.pause()

      this.previewStart = start
      this.previewEnd = end
      this.segmentIndex = index

      this.$refs.audioPreview.currentTime = this.previewStart
      this.$refs.audioPreview.play()
    },
    getAudioUrl: function () {
      let distribution = _.find(this.distributions, item => item._id === this.lyricVideo.distribution)
      let song = _.find(distribution.songs, (item, index) => index === parseInt(this.currentSong))

      return song.file_url_unencoded || song.file_url
    },
    getLyrics: function () {
      this.lyricTranscription = {}
      this.transcriptionLoaded = false

      if (this.lyricVideo.song_index === undefined) {
        return
      }

      LyricVideo.getLyrics(this.lyricVideo.distribution, this.lyricVideo.song_index).then(response => {
        this.lyricTranscription = response.data

        if (!this.lyricVideo.status) {
          this.lyricVideo.status = 0
        }

        this.transcriptionLoaded = true
      }).catch(() => {
        this.transcriptionLoaded = true
      })
    },
    getReleases: function () {
      this.loading = true

      return Distributions.getAll({ status: [2, 3] }).then(response => {
        this.distributions = _.filter(response.data, item => item.has_lyrics)
        this.distributionOptions = _.map(this.distributions, item => {
          return { id: item._id, text: `${item.artist.name} - ${item.title}` }
        })

        this.loading = false
      })
    },
    getLyricVideo: function () {
      LyricVideo.get(this.$route.params.id).then(response => {
        this.lyricVideo = response.data
        this.lyricVideo.color = this.lyricVideo.color || 1
      }).catch((err) => {
        console.log('Error getting lyric video', err)
      })
    }
  },
  computed: {
    songs: function () {
      let songs = []

      if (this.lyricVideo.distribution) {
        let distro = _.find(this.distributions, item => item._id === this.lyricVideo.distribution)

        _.each(distro.songs, (item, index) => {
          songs.push({
            id: index,
            text: item.title
          })
        })
      }

      return songs
    }
  },
  mounted: function () {
  },
  beforeDestroy: function () {
  },
  created: function () {
    this.user = this.$user

    this.getReleases().then(() => {
      if (this.$route.params.id !== 'new') {
        this.getLyricVideo()
      }
    })
  },
  watch: {
    'lyricVideo.distribution': function (newVal, oldVal) {
      this.currentSong = 0
      this.getLyrics()
    },
    currentSong: function (newVal) {
      this.lyricVideo.song_index = newVal
      this.getLyrics()
    },
    $route: function () {
      if (this.$route.params.id !== 'new') {
        this.getLyricVideo()
      }
    }
  },
  components: {
    Select2
  }
}
</script>
<style lang="scss" scoped>
.preview-audio {
  width: 100%;
}

.badge-pro {
  border-radius: 3px;
}

.nav-item {
  cursor: pointer;
}

video {
  border-radius: 3px;
}

.color-1 {
  color: #d32d5f;
}

.color-2 {
  color: #3419b4;
}

.color-3 {
  color: #58bab9;
}

.color-4 {
  color: #7688f8;
}

.color-5 {
  color: #c49dec;
}

.color-6 {
  color: #000000;
}
</style>



// WEBPACK FOOTER //
// src/components/LyricVideoProcess.vue